import { useCallback, useMemo } from 'react';
import { useRecoilState, type SetterOrUpdater } from 'recoil';
import type {
  FeedbackInputValidationType,
  ValidationError,
  ValidationField,
  ValidationState,
} from '../../state/validationState';
import validationState from '../../state/validationState';
import clearError from './utils/clearError';

const VALIDATION_FIELDS: Array<ValidationField> = [
  'criteria',
  'customReason',
  'rescheduledDate',
  'snoozeDate',
  'snoozeNote',
];

export type ClearValidationErrorArgs = {
  key: FeedbackInputValidationType;
  field: ValidationField;
};

type UseFeedbackValidationReturnType = {
  validationErrors: ValidationState;
  errorsToShowArray: Array<ValidationError>;
  errorsToShowLookup: Partial<Record<ValidationField, Array<ValidationError>>>;
  setValidationErrors: SetterOrUpdater<ValidationState>;
  clearValidationErrorForField: (args: ClearValidationErrorArgs) => void;
  clearValidationErrors: () => void;
};

type Args = {
  validationFor?: Array<FeedbackInputValidationType>;
};

const useFeedbackValidation = ({
  validationFor = [],
}: Args): UseFeedbackValidationReturnType => {
  const [validationErrors, setValidationErrors] =
    useRecoilState(validationState);

  const errorsToShowArray: Array<ValidationError> = useMemo(() => {
    if (validationFor.length === 0) {
      // If no specific types are specified, return all errors
      return Array.from(validationErrors.values()).flat();
    }
    // Only include errors for the specified types
    return validationFor.map(type => validationErrors.get(type) ?? []).flat();
  }, [validationErrors, validationFor]);

  const errorsToShowLookup: Partial<
    Record<ValidationField, Array<ValidationError>>
  > = useMemo(() => {
    const lookup: Partial<Record<ValidationField, Array<ValidationError>>> = {};
    errorsToShowArray.forEach(error => {
      const field = error.field as ValidationField;
      if (Object.values(VALIDATION_FIELDS).includes(field)) {
        if (!lookup[field]) {
          lookup[field] = [];
        }
        lookup[field]!.push(error);
      }
    });
    return lookup;
  }, [errorsToShowArray]);

  const clearValidationErrorForField = ({
    key,
    field,
  }: ClearValidationErrorArgs) => {
    clearError({
      state: validationErrors,
      setState: setValidationErrors,
      key,
      field,
    });
  };

  const clearValidationErrors = useCallback(() => {
    setValidationErrors(new Map());
  }, [setValidationErrors]);

  return {
    validationErrors,
    errorsToShowArray,
    errorsToShowLookup,
    setValidationErrors,
    clearValidationErrorForField,
    clearValidationErrors,
  };
};

export default useFeedbackValidation;
